import { useState, useEffect } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import { Navigate } from 'react-router-dom';
import { Loader2, AlertCircle, Eye, EyeOff, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';
import logo from '../assets/logo.png';
import { usePageTitle } from '../lib/usePageTitle';
import { firstAccessibleStaffPath } from '../lib/access-control';

export function AdminLogin() {
    usePageTitle('Staff Sign In — Print My Parts');
    const { session, userRole, permissions, signOut, signInWithGoogle } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    // If a customer account logs in on the admin portal, sign them out immediately
    useEffect(() => {
        if (session && userRole === 'customer') {
            signOut();
            setError('This portal is restricted to Print My Parts staff only.');
        }
    }, [session, userRole, signOut]);

    // Redirect authorized staff to the dashboard
    if (session && userRole !== null && userRole !== 'customer') {
        return <Navigate to={userRole === 'admin' ? '/' : firstAccessibleStaffPath(permissions) || '/'} replace />;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setMessage(null);

        try {
            if (isForgotPassword) {
                const { error } = await supabase.auth.resetPasswordForEmail(email, {
                    redirectTo: window.location.origin + '/reset-password',
                });
                if (error) throw error;
                setMessage('Check your email for a password reset link.');
            } else {
                const { error } = await supabase.auth.signInWithPassword({ email, password });
                if (error) throw error;
            }
        } catch (err: any) {
            setError(err.message || 'Authentication failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError(null);
        await signInWithGoogle();
    };

    return (
        <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-slate-950">
            {/* Ambient background glow */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[10%] w-[400px] h-[400px] bg-violet-600/8 rounded-full blur-[100px]" />
            </div>

            {/* Subtle grid */}
            <div
                className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
                    backgroundSize: '48px 48px',
                }}
            />

            {/* Card */}
            <div className="relative z-10 w-full max-w-md mx-4">
                {/* Logo & Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-900 border border-slate-700/50 shadow-xl mb-5">
                        <img src={logo} alt="Print My Parts" className="h-9 w-auto" />
                    </div>
                    <h1 className="text-2xl font-bold text-white tracking-tight">Staff Portal</h1>
                    <p className="text-slate-400 text-sm mt-1.5">Sign in to manage production & administration</p>
                </div>

                <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
                    {/* Error / Success */}
                    {error && (
                        <div className="mb-5 p-3.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl flex items-center gap-2.5 text-sm">
                            <AlertCircle size={16} className="shrink-0" />
                            {error}
                        </div>
                    )}
                    {message && (
                        <div className="mb-5 p-3.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl flex items-center gap-2.5 text-sm">
                            <AlertCircle size={16} className="shrink-0" />
                            {message}
                        </div>
                    )}

                    {!isForgotPassword && (
                        <>
                            {/* Google SSO */}
                            <button
                                type="button"
                                onClick={handleGoogleSignIn}
                                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white/5 hover:bg-white/10 border border-slate-700/60 hover:border-slate-600 text-white text-sm font-medium rounded-xl transition-all mb-5"
                            >
                                <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5" alt="Google" />
                                Continue with Google
                            </button>

                            <div className="relative mb-5">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-slate-700/60" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-slate-900 px-3 text-slate-500 tracking-wider">Or with email</span>
                                </div>
                            </div>
                        </>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email</label>
                            <input
                                required
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="you@printmyparts.io"
                                maxLength={255}
                                className="w-full px-4 py-3 bg-slate-800/80 border border-slate-700/60 text-white placeholder-slate-500 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/60 transition-all text-sm"
                            />
                        </div>

                        {!isForgotPassword && (
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Password</label>
                                    <button
                                        type="button"
                                        onClick={() => { setIsForgotPassword(true); setError(null); setMessage(null); }}
                                        className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                                <div className="relative">
                                    <input
                                        required
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        maxLength={128}
                                        className="w-full px-4 py-3 pr-12 bg-slate-800/80 border border-slate-700/60 text-white placeholder-slate-500 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/60 transition-all text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all shadow-lg shadow-indigo-500/20 mt-2"
                        >
                            {isLoading
                                ? <Loader2 size={18} className="animate-spin" />
                                : isForgotPassword ? 'Send Reset Link' : 'Sign In'
                            }
                        </button>

                        {isForgotPassword && (
                            <button
                                type="button"
                                onClick={() => { setIsForgotPassword(false); setError(null); setMessage(null); }}
                                className="w-full text-sm text-slate-400 hover:text-slate-200 transition-colors"
                            >
                                ← Back to sign in
                            </button>
                        )}
                    </form>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-center gap-2 mt-6 text-slate-600 text-xs">
                    <Shield size={12} />
                    <span>Restricted to authorized Print My Parts staff</span>
                </div>
            </div>
        </div>
    );
}
